/**
 * Sessions Page
 * Displays and manages user sessions
 * Users can view and terminate their own sessions
 * Admins can view all sessions for their tenant
 */

import { useEffect, useState } from 'react'
import { Monitor, Shield, AlertTriangle, RefreshCw, Filter } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { useSessionStore } from '@/store/sessionStore'
import { toast } from '@/store/toastStore'
import { SessionList } from '@/components/sessions'
import type { SessionStatus } from '@/types/session.types'

// ============================================================================
// TYPES
// ============================================================================

interface SessionFilters {
  status: SessionStatus | 'all'
  showOtherUsers: boolean
}

// ============================================================================
// STATS CARD COMPONENT
// ============================================================================

function StatsCard({
  label,
  value,
  icon: Icon,
  color,
}: {
  label: string
  value: number
  icon: React.ElementType
  color: 'blue' | 'green' | 'orange' | 'red'
}) {
  const colorClasses = {
    blue: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
    green: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400',
    orange: 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400',
    red: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400',
  }

  return (
    <div className="card card-elevated p-4 hover:shadow-lg transition">
      <div className="flex items-center gap-4">
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
        <div>
          <p className="text-text-secondary text-sm font-medium">{label}</p>
          <p className="text-text-primary text-2xl font-bold">{value}</p>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function SessionsPage() {
  const { user: currentUser, session: authSession, hasPermission } = useAuth()
  const canViewAllSessions = hasPermission('txh.session.read_any')
  const canRevoke = hasPermission('txh.session.revoke')
  const canRevokeAll = hasPermission('txh.session.revoke_all')

  // Store
  const {
    sessions,
    isLoading,
    loadUserSessions,
    loadAllSessions,
    terminateSession,
    terminateAllOtherSessions,
  } = useSessionStore()

  // Filters
  const [filters, setFilters] = useState<SessionFilters>({
    status: 'all',
    showOtherUsers: false,
  })
  const [isRefreshing, setIsRefreshing] = useState(false)

  // Load sessions on mount
  useEffect(() => {
    loadData()
  }, [currentUser?.id, currentUser?.tenantId, filters.showOtherUsers])

  const loadData = async () => {
    if (!currentUser) return

    if (filters.showOtherUsers && canViewAllSessions && currentUser.tenantId) {
      // Mock data is single-tenant, so "all" == tenant scope here.
      await loadAllSessions()
    } else {
      await loadUserSessions(currentUser.id)
    }
  }

  const handleRefresh = async () => {
    setIsRefreshing(true)
    await loadData()
    setIsRefreshing(false)
  }

  // Filter sessions
  const filteredSessions = sessions.filter(s => {
    if (filters.status !== 'all' && s.status !== filters.status) return false
    return true
  })

  // Calculate stats
  const stats = {
    total: sessions.length,
    active: sessions.filter(s => s.status === 'active').length,
    expired: sessions.filter(s => s.status === 'expired').length,
    revoked: sessions.filter(s => s.status === 'revoked').length,
  }

  // Current session ID
  const currentSessionId = authSession.sessionId

  // Handlers
  const handleTerminate = async (sessionId: string) => {
    if (!canRevoke || filters.showOtherUsers) return
    if (
      window.confirm(
        'Are you sure you want to end this session? The user will be logged out from this device.'
      )
    ) {
      try {
        await terminateSession(sessionId)
        toast.success('Session ended', 'The device has been signed out.')
      } catch {
        toast.error('Could not end session', 'Please try again.')
      }
    }
  }

  const handleTerminateAll = async () => {
    if (!canRevokeAll || filters.showOtherUsers || !currentUser) return
    if (
      window.confirm(
        'Are you sure you want to end all other sessions? You will remain logged in only on this device.'
      )
    ) {
      try {
        await terminateAllOtherSessions(currentUser.id, currentSessionId || '')
        toast.success('Other sessions ended', 'You remain signed in on this device.')
      } catch {
        toast.error('Could not end sessions', 'Please try again.')
      }
    }
  }

  return (
    <div className="p-6 space-y-6 max-w-5xl mx-auto">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-text-primary">Sessions</h1>
          <p className="text-text-secondary mt-1">
            {filters.showOtherUsers
              ? 'Manage all active sessions in your organization'
              : 'Manage your active sessions and devices'}
          </p>
        </div>
        <button
          type="button"
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="btn btn-outline gap-2"
        >
          <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard label="Total Sessions" value={stats.total} icon={Monitor} color="blue" />
        <StatsCard label="Active" value={stats.active} icon={Shield} color="green" />
        <StatsCard label="Expired" value={stats.expired} icon={AlertTriangle} color="orange" />
        <StatsCard label="Revoked" value={stats.revoked} icon={AlertTriangle} color="red" />
      </div>

      {/* Filters */}
      <div className="card card-elevated p-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-text-tertiary" />
            <span className="text-sm font-medium text-text-primary">Filters</span>
          </div>

          <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
            {/* Status Filter */}
            <select
              value={filters.status}
              onChange={e =>
                setFilters(f => ({ ...f, status: e.target.value as SessionFilters['status'] }))
              }
              className="input input-bordered w-full sm:w-auto"
              aria-label="Session status"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="expired">Expired</option>
              <option value="revoked">Revoked</option>
            </select>

            {/* Admin: Show all users toggle */}
            {canViewAllSessions && (
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.showOtherUsers}
                  onChange={e => setFilters(f => ({ ...f, showOtherUsers: e.target.checked }))}
                  className="h-4 w-4 rounded border-surface-overlay accent-primary-600"
                />
                <span className="text-sm text-text-secondary">Show all users</span>
              </label>
            )}
          </div>

          {/* Result count */}
          <div className="sm:ml-auto text-sm text-text-tertiary">
            Showing {filteredSessions.length} of {sessions.length} sessions
          </div>
        </div>
      </div>

      {/* Security Notice */}
      <div className="card card-elevated p-4 border-l-4 border-primary-500 bg-primary-50/50 dark:bg-primary-900/10">
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-text-primary">Security Tip</h3>
            <p className="text-sm text-text-secondary mt-1">
              If you notice any unfamiliar sessions, end them immediately and consider changing your
              password. Active sessions show where you're currently logged in.
            </p>
          </div>
        </div>
      </div>

      {/* Session List */}
      <SessionList
        sessions={filteredSessions}
        currentSessionId={currentSessionId ?? undefined}
        onTerminate={canRevoke && !filters.showOtherUsers ? handleTerminate : undefined}
        onTerminateAll={canRevokeAll && !filters.showOtherUsers ? handleTerminateAll : undefined}
        isLoading={isLoading}
        showTerminateAll={canRevokeAll && !filters.showOtherUsers}
      />

      {/* Help Text */}
      <div className="text-center text-sm text-text-tertiary">
        <p>
          Sessions are automatically expired after 24 hours of inactivity.
          <br />
          You can end sessions manually by clicking the logout button on each card.
        </p>
      </div>
    </div>
  )
}
